import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useSubmitFranchiseForm } from "../hooks/useSubmitFranchiseForm";
import Icon from "../../../common/components/Icon/Icon";
import {
  createFranchiseFormSchema,
  FranchiseFormData,
} from "../helpers/franchiseValidation";

const FranchiseForm: React.FC = () => {
  const { t } = useTranslation();
  const { submitFranchiseForm, isLoading, isSuccess, isError } =
    useSubmitFranchiseForm();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FranchiseFormData>({
    resolver: zodResolver(createFranchiseFormSchema(t)),
  });

  useEffect(() => {
    register("formLoadTimestamp");
    setValue("formLoadTimestamp", Math.floor(Date.now() / 1000));
  }, [register, setValue]);

  const onSubmit = async (data: FranchiseFormData) => {
    await submitFranchiseForm(data);
  };

  if (isSuccess) {
    return (
      <div className="ff-success-state">
        <div className="ff-success-state__icon">
          <Icon iconName="check-circle" className="icon--filled" />
        </div>
        <h3 className="ff-success-state__title">
          {t("feedback.default.success", "Application Received!")}
        </h3>
        <p className="ff-success-state__message">
          {t(
            "franchiseForm.successMessage",
            "Thank you for your interest. We will evaluate your application and contact you."
          )}
        </p>
      </div>
    );
  }

  return (
    <form
      className={`ff-form ${isLoading ? "is-loading" : ""}`}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="ff-form__header">
        <h2 className="ff-form__title">
          {t("franchiseForm.title", "Franchise Application Form")}
        </h2>
      </div>

      {isError && (
        <div className="ff-form__alert ff-form__alert--error">
          <Icon iconName="exclamation-circle" className="icon--filled" />
          <span>
            {t(
              "franchiseForm.error",
              "Failed to submit. Please check your inputs."
            )}
          </span>
        </div>
      )}

      <fieldset className="ff-form__fieldset" disabled={isLoading}>
        {/* Honeypot */}
        <div className="ff-form__honeypot" aria-hidden="true">
          <label htmlFor="ff_nickname">Nickname</label>
          <input
            id="ff_nickname"
            type="text"
            {...register("nickname")}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <div className="ff-form__row">
          <div className="ff-field">
            <label className="ff-field__label" htmlFor="ff-name">
              {t("franchiseForm.name", "Name")}{" "}
              <span className="required-mark">*</span>
            </label>
            <input
              id="ff-name"
              type="text"
              {...register("name")}
              className={`ff-field__input ${errors.name ? "has-error" : ""}`}
            />
            {errors.name && (
              <p className="ff-field__error">{errors.name.message}</p>
            )}
          </div>

          <div className="ff-field">
            <label className="ff-field__label" htmlFor="ff-surname">
              {t("franchiseForm.surname", "Surname")}{" "}
              <span className="required-mark">*</span>
            </label>
            <input
              id="ff-surname"
              type="text"
              {...register("surname")}
              className={`ff-field__input ${errors.surname ? "has-error" : ""}`}
            />
            {errors.surname && (
              <p className="ff-field__error">{errors.surname.message}</p>
            )}
          </div>
        </div>

        <div className="ff-form__row">
          <div className="ff-field">
            <label className="ff-field__label" htmlFor="ff-email">
              {t("franchiseForm.email", "Email")}{" "}
              <span className="required-mark">*</span>
            </label>
            <input
              id="ff-email"
              type="email"
              {...register("email")}
              className={`ff-field__input ${errors.email ? "has-error" : ""}`}
            />
            {errors.email && (
              <p className="ff-field__error">{errors.email.message}</p>
            )}
          </div>

          <div className="ff-field">
            <label className="ff-field__label" htmlFor="ff-phone">
              {t("franchiseForm.phone", "Phone")}{" "}
              <span className="required-mark">*</span>
            </label>
            <input
              id="ff-phone"
              type="tel"
              {...register("phone")}
              className={`ff-field__input ${errors.phone ? "has-error" : ""}`}
            />
            {errors.phone && (
              <p className="ff-field__error">{errors.phone.message}</p>
            )}
          </div>
        </div>

        <div className="ff-form__row">
          <div className="ff-field">
            <label className="ff-field__label" htmlFor="ff-city">
              {t("franchiseForm.city", "Franchise City")}{" "}
              <span className="required-mark">*</span>
            </label>
            <input
              id="ff-city"
              type="text"
              {...register("city")}
              className={`ff-field__input ${errors.city ? "has-error" : ""}`}
            />
            {errors.city && (
              <p className="ff-field__error">{errors.city.message}</p>
            )}
          </div>

          <div className="ff-field">
            <label className="ff-field__label" htmlFor="ff-district">
              {t("franchiseForm.district", "Franchise District")}{" "}
              <span className="required-mark">*</span>
            </label>
            <input
              id="ff-district"
              type="text"
              {...register("district")}
              className={`ff-field__input ${errors.district ? "has-error" : ""}`}
            />
            {errors.district && (
              <p className="ff-field__error">{errors.district.message}</p>
            )}
          </div>
        </div>

        <div className="ff-field">
          <label className="ff-field__label" htmlFor="ff-message">
            {t("franchiseForm.message", "Message (Optional)")}
          </label>
          <textarea
            id="ff-message"
            rows={4}
            {...register("message")}
            className="ff-field__input"
          />
        </div>

        <button type="submit" className="ff-form__submit">
          {isLoading
            ? t("franchiseForm.sending", "Sending...")
            : t("franchiseForm.submit", "Submit Application")}
        </button>
      </fieldset>
    </form>
  );
};

export default FranchiseForm;
